import { useState, useMemo } from "react";
import { TrendingUp, TrendingDown, DollarSign, Calendar, AlertCircle } from "lucide-react";
import { useFluxoCaixa } from "@/features/financeiro/hooks/useFluxoCaixa";
import { useFluxoCaixaProjecao } from "@/features/financeiro/hooks/useRelatorios";
import { useContasPagar } from "@/features/financeiro/hooks/useContasPagar";
import { useContasReceber } from "@/features/financeiro/hooks/useContasReceber";
import { useFinanceKPIs } from "@/features/financeiro/hooks/useFinanceKPIs";
import { FinanceKPIs } from "@/features/financeiro/components/FinanceKPIs";
import { FinanceAlerts } from "@/features/financeiro/components/FinanceAlerts";
import { CashFlowProjection } from "@/features/financeiro/components/CashFlowProjection";
import { MonthlyComparison } from "@/features/financeiro/components/MonthlyComparison";
import { MultiCenarioForecast } from "@/features/financeiro/components/MultiCenarioForecast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { startOfMonth, endOfMonth, format, subMonths, addDays } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function fmt(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const FluxoCaixa = () => {
  const [periodo, setPeriodo] = useState("mes");
  const [agrupamento, setAgrupamento] = useState<'dia' | 'semana' | 'mes'>('dia');

  const now = new Date();
  const startDate = useMemo(() => {
    switch (periodo) {
      case "3meses":
        return format(subMonths(now, 3), "yyyy-MM-dd");
      case "6meses":
        return format(subMonths(now, 6), "yyyy-MM-dd");
      case "ano":
        return format(subMonths(now, 12), "yyyy-MM-dd");
      default:
        return format(startOfMonth(now), "yyyy-MM-dd");
    }
  }, [periodo]);
  
  const endDate = format(endOfMonth(now), "yyyy-MM-dd");
  
  const {
    isLoading,
    totalEntradas,
    totalSaidas,
    totalAPagar,
    totalAReceber,
    saldoAtual,
    saldoPrevisto,
    fluxoData,
    despesasData,
  } = useFluxoCaixa(startDate, endDate);

  const { contas: contasPagar } = useContasPagar({ tamanhoPagina: 100 });
  const { contas: contasReceber } = useContasReceber({ tamanhoPagina: 100 });

  const kpis = useFinanceKPIs({ contasPagar, contasReceber });

  const { data: projecao, isLoading: isLoadingProjecao } =
    useFluxoCaixaProjecao(startDate, endDate, agrupamento);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fluxo de Caixa</h1>
          <p className="text-muted-foreground mt-1">
            Análise de entradas e saídas financeiras
          </p>
        </div>
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mes">Último mês</SelectItem>
            <SelectItem value="3meses">Últimos 3 meses</SelectItem>
            <SelectItem value="6meses">Últimos 6 meses</SelectItem>
            <SelectItem value="ano">Último ano</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <FinanceAlerts
        contasPagar={contasPagar}
        contasReceber={contasReceber}
        saldoPrevisto={saldoPrevisto}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total Entradas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {totalEntradas.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Período selecionado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Total Saídas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {totalSaidas.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Período selecionado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Saldo Realizado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${saldoAtual >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {saldoAtual.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Entradas - Saídas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              A Pagar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {totalAPagar.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Pendente + Atrasado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Saldo Previsto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${saldoPrevisto >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {saldoPrevisto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">A Receber - A Pagar</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Evolução do Fluxo de Caixa</CardTitle>
          <CardDescription>
            Entradas e saídas realizadas (pagamentos e recebimentos confirmados)
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Carregando...
            </div>
          ) : fluxoData.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Nenhuma transação encontrada para o período
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={fluxoData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [
                    value.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }),
                  ]}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="entradas" 
                  stroke="hsl(var(--chart-1))" 
                  strokeWidth={2}
                  name="Entradas"
                />
                <Line 
                  type="monotone" 
                  dataKey="saidas" 
                  stroke="hsl(var(--chart-2))" 
                  strokeWidth={2}
                  name="Saídas"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Despesas por Categoria</CardTitle>
            <CardDescription>
              Distribuição dos gastos realizados (contas pagas + transações de saída)
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Carregando...
              </div>
            ) : despesasData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhuma despesa encontrada para o período
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={despesasData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="categoria" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [
                      value.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }),
                      "Valor",
                    ]}
                  />
                  <Bar dataKey="valor" fill="hsl(var(--chart-1))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resumo de Despesas</CardTitle>
            <CardDescription>Total por categoria</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Carregando...
              </div>
            ) : despesasData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhuma despesa para exibir
              </div>
            ) : (
              <div className="space-y-4">
                {despesasData.map((cat, idx) => (
                  <div key={cat.categoria} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ 
                          backgroundColor: `hsl(var(--chart-${(idx % 5) + 1}))` 
                        }}
                      />
                      <span className="font-medium text-foreground">{cat.categoria}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-foreground">
                        {cat.valor.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {((cat.valor / totalSaidas) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">Total</span>
                    <span className="font-bold text-foreground text-lg">
                      {totalSaidas.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <FinanceKPIs {...kpis} />

      {/* ── Projeção Multi-Cenário (Sprint 2F) ── */}
      <Card>
        <CardHeader>
          <CardTitle>Projeção Multi-Cenário (60 dias)</CardTitle>
          <CardDescription>
            Comparação entre cenários Pessimista, Realista e Otimista — ajuste os parâmetros conforme sua estratégia.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MultiCenarioForecast
            dtInicio={format(now, "yyyy-MM-dd")}
            dtFim={format(addDays(now, 60), "yyyy-MM-dd")}
            agrupamento={agrupamento}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CashFlowProjection
          contasPagar={contasPagar}
          contasReceber={contasReceber}
          saldoAtual={saldoAtual}
        />
        <MonthlyComparison
          contasPagar={contasPagar}
          contasReceber={contasReceber}
        />
      </div>

      {/* ── Projeção Detalhada por Período (backend) ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Projeção Detalhada por Período</CardTitle>
              <CardDescription>
                Realizado (MovCaixa) vs. Previsto (Contas a Pagar/Receber) agrupados
              </CardDescription>
            </div>
            <Select value={agrupamento} onValueChange={v => setAgrupamento(v as 'dia' | 'semana' | 'mes')}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dia">Por Dia</SelectItem>
                <SelectItem value="semana">Por Semana</SelectItem>
                <SelectItem value="mes">Por Mês</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead className="text-right text-green-600">Ent. Realizadas</TableHead>
                  <TableHead className="text-right text-red-600">Saí. Realizadas</TableHead>
                  <TableHead className="text-right text-blue-600">Ent. Previstas</TableHead>
                  <TableHead className="text-right text-orange-600">Saí. Previstas</TableHead>
                  <TableHead className="text-right">Saldo Período</TableHead>
                  <TableHead className="text-right">Saldo Acumulado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingProjecao
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : !projecao || projecao.periodos.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Nenhum dado para o período selecionado.
                        </TableCell>
                      </TableRow>
                    )
                  : projecao.periodos.map(p => (
                      <TableRow key={p.label}>
                        <TableCell className="font-medium">{p.label}</TableCell>
                        <TableCell className="text-right text-green-600">{fmt(p.entradasRealizadas)}</TableCell>
                        <TableCell className="text-right text-red-600">{fmt(p.saidasRealizadas)}</TableCell>
                        <TableCell className="text-right text-blue-600">{fmt(p.entradasPrevistas)}</TableCell>
                        <TableCell className="text-right text-orange-600">{fmt(p.saidasPrevistas)}</TableCell>
                        <TableCell className={`text-right font-medium ${p.saldoPeriodo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmt(p.saldoPeriodo)}
                        </TableCell>
                        <TableCell className={`text-right font-semibold ${p.saldoAcumulado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {fmt(p.saldoAcumulado)}
                        </TableCell>
                      </TableRow>
                    ))
                }
                {projecao && projecao.periodos.length > 0 && (
                  <TableRow className="border-t-2 bg-muted/30 font-semibold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right text-green-600">{fmt(projecao.totalEntradasRealizadas)}</TableCell>
                    <TableCell className="text-right text-red-600">{fmt(projecao.totalSaidasRealizadas)}</TableCell>
                    <TableCell className="text-right text-blue-600">{fmt(projecao.totalEntradasPrevistas)}</TableCell>
                    <TableCell className="text-right text-orange-600">{fmt(projecao.totalSaidasPrevistas)}</TableCell>
                    <TableCell className="text-right" />
                    <TableCell className={`text-right ${projecao.saldoFinal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {fmt(projecao.saldoFinal)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FluxoCaixa;
